import { CommonModule } from '@angular/common';
import { Component, Inject, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ServiceHandler } from '@nucleus/ui-base';
import { PageHeaderComponent } from "@nucleus/ui-base";
import { UserDataTypeService } from '../../services/userdata-type/userdata-type.service';
import { PermissionCheckerService } from '../../services/permission/permission-checker.service';
import {
    UserDataTypeTemplate,
    UserDataTypeTemplateCreateRequest,
    UserDataTypeTemplateUpdateRequest,
} from '../../services/userdata-type/userdata-type.models';

export interface UserDataTypeTemplateDialogData {
    /** Owning (source) type — the type whose rows the template will hydrate. */
    sourceType: string;
    /** Provided when editing; null when creating. */
    template: UserDataTypeTemplate | null;
    /** Distinct types observed in the system, used to autocomplete the
     *  templateType picker. Optional; if absent the input is free-text. */
    knownTypes?: string[];
}

/**
 * Create-or-edit dialog for a user_data_type_template rule.
 *
 * On create: full body — referencePath, templateType, fieldPath, targetPath,
 *            overwrite, description. POST as source=MANUAL.
 * On edit:   only overwrite + description are mutable; the four path/type
 *            fields are the rule's identity and are read-only. Lock/unlock
 *            handles the source flip from the row action, not from this form.
 */
@Component({
    selector: 'userdata-type-template-dialog',
    standalone: true,
    imports: [
        CommonModule, FormsModule, ReactiveFormsModule,
        MatDialogModule, MatFormFieldModule, MatInputModule,
        MatButtonModule, MatIconModule, MatSelectModule, MatSlideToggleModule,
        MatAutocompleteModule, PageHeaderComponent,
    ],
    templateUrl: './userdata-type-template-dialog.component.html',
    styleUrl: './userdata-type-template-dialog.component.css',
})
export class UserDataTypeTemplateDialogComponent {

    private userDataTypeService: UserDataTypeService = inject(UserDataTypeService);
    private permissionChecker: PermissionCheckerService = inject(PermissionCheckerService);
    private dialogRef: MatDialogRef<UserDataTypeTemplateDialogComponent, UserDataTypeTemplate | null>
        = inject(MatDialogRef);

    saving = false;
    errorMessage = '';
    form: FormGroup;
    isEdit = false;
    /** Filtered autocomplete options for the templateType field. */
    filteredTypes: string[] = [];

    constructor(@Inject(MAT_DIALOG_DATA) public data: UserDataTypeTemplateDialogData) {
        const t = data.template;
        this.isEdit = !!t;

        this.form = new FormGroup({
            referencePath: new FormControl(
                { value: t?.referencePath ?? '', disabled: this.isEdit },
                [Validators.required, Validators.maxLength(255)],
            ),
            templateType: new FormControl(
                { value: t?.templateType ?? '', disabled: this.isEdit },
                [Validators.required, Validators.maxLength(80)],
            ),
            fieldPath: new FormControl(
                { value: t?.fieldPath ?? '', disabled: this.isEdit },
                [Validators.required, Validators.maxLength(255)],
            ),
            targetPath: new FormControl(
                { value: t?.targetPath ?? '', disabled: this.isEdit },
                [Validators.required, Validators.maxLength(255)],
            ),
            overwrite: new FormControl(t?.overwrite ?? false),
            description: new FormControl(t?.description ?? ''),
        });

        this.filteredTypes = (data.knownTypes ?? []).slice();
        // Wire the templateType field's valueChanges to a simple
        // case-insensitive contains() — autocomplete is purely advisory; the
        // user can always type a not-yet-observed type, the backend will
        // accept the rule and inference will fill in once data arrives.
        this.form.controls['templateType'].valueChanges.subscribe((value: string | null) => {
            const q = (value ?? '').toLowerCase();
            this.filteredTypes = (data.knownTypes ?? [])
                .filter(name => name.toLowerCase().includes(q));
        });
    }

    cancel(): void {
        this.dialogRef.close(null);
    }

    save(): void {
        if (this.form.invalid) return;
        if (!this.permissionChecker.hasAccessToManageUserDataTypes()) { return; }
        this.saving = true;
        this.errorMessage = '';

        const v = this.form.getRawValue();
        const handler: ServiceHandler<UserDataTypeTemplate> = {
            onStart: () => {},
            onSuccess: (saved) => { this.dialogRef.close(saved); },
            onError: (err) => { this.errorMessage = (err ?? 'Save failed').toString(); },
            onComplete: () => { this.saving = false; },
        };

        if (this.isEdit && this.data.template) {
            const body: UserDataTypeTemplateUpdateRequest = {
                overwrite: !!v.overwrite,
                description: (v.description ?? '').trim() || undefined,
            };
            this.userDataTypeService.updateTemplate(
                handler, this.data.sourceType, this.data.template.targetPath, body,
            );
        } else {
            const body: UserDataTypeTemplateCreateRequest = {
                referencePath: (v.referencePath ?? '').trim(),
                templateType: (v.templateType ?? '').trim(),
                fieldPath: (v.fieldPath ?? '').trim(),
                targetPath: (v.targetPath ?? '').trim(),
                overwrite: !!v.overwrite,
                description: (v.description ?? '').trim() || undefined,
            };
            this.userDataTypeService.createTemplate(handler, this.data.sourceType, body);
        }
    }
}
